import { useCallback } from 'react';

import {
  Button,
  ButtonGroup,
  Divider,
  OutlineCircleQuestionIcon,
  Tooltip,
} from '@frontend/legacy-components';

import { getReasonText } from 'modules/common/entityActions';
import {
  ViewerPopoverButton,
  ViewerToolsBar,
} from 'modules/common/viewer/components';
import { useDxfViewerSession } from 'modules/common/viewer/dxfViewer/DxfViewerContext';
import { DxfSelectionModeGroup } from 'modules/common/viewer/dxfViewer/components/DxfSelectionModeGroup';
import { DxfUndoRedoGroup } from 'modules/common/viewer/dxfViewer/components/DxfUndoRedoGroup';
import { DxfViewerControlsInfoRows } from 'modules/common/viewer/dxfViewer/components/DxfViewerControlsInfoRows';
import { useDxfSelectionMode } from 'modules/common/viewer/dxfViewer/hooks/useDxfSelectionMode';
import { useDxfMappingActions } from 'modules/common/viewer/dxfViewer/mapping/mappingActions';
import { useReadableStore } from 'modules/common/viewer/dxfViewer/session/useSessionState';

import { isMobileBrowser } from 'utils/browserUtils';

import { useDxfMappingSubject } from '../hooks/useDxfMappingSubject';
import { DxfBrushToolGroup } from './DxfBrushToolGroup';

export type DxfMappingToolbarProps = {
  /** Leaves mapping mode, confirming first when there are edits to discard. */
  onCancel: () => void;
};

export const DxfMappingToolbar = ({ onCancel }: DxfMappingToolbarProps) => {
  const session = useDxfViewerSession();
  // Draft editing (selection, brushes, undo/redo) answers to the session's lock,
  // which the brush and shortcuts also enforce; Save is the `dxfMapping.save`
  // action.
  const lockReason = useReadableStore(
    session.mapping,
    (current) => current.lockReason,
  );
  const canUndo = useReadableStore(session.mappingHistory.canUndo);
  const canRedo = useReadableStore(session.mappingHistory.canRedo);
  const tool = useReadableStore(session.tools);
  const selectionMode = useDxfSelectionMode();
  const isMobile = isMobileBrowser();
  const isLocked = lockReason !== undefined;
  const mapping = useDxfMappingSubject(session);
  const { save } = useDxfMappingActions();

  const canSave = mapping !== null && save.getCanExecute(mapping);
  const isSaving = mapping !== null && save.getIsPending(mapping);
  const saveDisabledReason =
    mapping === null || isSaving
      ? undefined
      : getReasonText(save.getDisabledReason(mapping));
  const saveLabel =
    saveDisabledReason === undefined
      ? 'Save Mapping'
      : `Save Mapping. ${saveDisabledReason}`;
  const handleSave = useCallback(() => {
    if (mapping) {
      save.execute(mapping);
    }
  }, [mapping, save]);

  return (
    <ViewerToolsBar px={0.75} data-testid="dxf-mapping-toolbar">
      <DxfSelectionModeGroup
        {...selectionMode}
        isDisabled={isLocked}
        disabledReason={lockReason}
      />

      <DxfBrushToolGroup
        activeTool={tool}
        onArmBrush={session.tools.armBrush}
        onChoose={() => session.tools.set('select')}
        isDisabled={isLocked}
        disabledReason={lockReason}
      />

      <DxfUndoRedoGroup
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={session.mappingHistory.undo}
        onRedo={session.mappingHistory.redo}
        isDisabled={isLocked}
      />

      <Divider orientation="vertical" h={3.5} borderColor="separator.divider" />

      <ViewerPopoverButton
        title="Mapping controls"
        icon={<OutlineCircleQuestionIcon boxSize={3} />}
      >
        <DxfViewerControlsInfoRows isMobile={isMobile} isMapping />
      </ViewerPopoverButton>

      <ButtonGroup spacing={1}>
        <Button
          size="md"
          variant="secondary"
          onClick={onCancel}
          data-testid="dxf-mapping-cancel"
        >
          Cancel
        </Button>
        <Tooltip
          placement="bottom"
          label={saveDisabledReason}
          isDisabled={saveDisabledReason === undefined}
        >
          <Button
            size="md"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={!canSave}
            aria-label={saveLabel}
            data-testid="dxf-mapping-save"
          >
            Save Mapping
          </Button>
        </Tooltip>
      </ButtonGroup>
    </ViewerToolsBar>
  );
};
DxfMappingToolbar.displayName = 'DxfMappingToolbar';
